/**
 * The uploaded-spreadsheet parser behind the bulk-upload modals. Its own module
 * so the SheetJS library reaches the browser only when a staff member actually
 * uploads a file — a value import of `xlsx` from `utils.ts`, which nearly every
 * Staff Site file imports, puts the whole library in the main bundle and
 * defeats the chart export's lazy chunk too (PRO-1507).
 */
import type { WorkBook, WorkSheet } from 'xlsx';

export class SpreadsheetParseError extends Error {}

/**
 * The header names of a worksheet, in column order — read from the header row,
 * not a data row, since sheet_to_json omits the key for an empty cell
 * (PRO-1335). NOT trimmed, so they stay identical to the row keys
 * `getParameterCaseInsensitive` later looks values up by.
 */
function spreadsheetHeaderNames(XLSX: typeof import('xlsx'), ws: WorkSheet): string[] {
  if (!ws['!ref']) { return []; }

  const range = XLSX.utils.decode_range(ws['!ref']);
  const headerNames: string[] = [];
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cell = ws[XLSX.utils.encode_cell({ r: range.s.r, c: col })];
    if (cell && (cell.v !== undefined) && (cell.v !== null)) {
      headerNames.push(String(cell.v));
    }
  }

  return headerNames;
}

/**
 * Parse an uploaded .csv/.xlsx file, check its required columns (a nested
 * array = accepted alternatives), and hand each populated row to
 * `processRowFunc`. `rowIdx` is 0 for the row after the header and COUNTS
 * blank rows, so `rowIdx + 2` is the row the staff member sees.
 *
 * @throws SpreadsheetParseError - Library chunk unreachable, unreadable file,
 *   unsupported type, or a missing column.
 */
export async function parseSpreadsheet(fileArrayBuf: ArrayBuffer,
                                       requiredColumnNames: (string | string[])[],
                                       processRowFunc: (rowIdx: number, rowItem: any) => void) {
  let XLSX: typeof import('xlsx');

  try {
    XLSX = await import('xlsx');
  } catch (e) {
    // A failed chunk fetch would otherwise surface as a webpack message.
    throw new SpreadsheetParseError(
      'Spreadsheet support could not be loaded. Please check your connection and try again.'
    );
  }

  let wb: WorkBook;

  try {
    wb = XLSX.read(fileArrayBuf);
  } catch (e) {
    throw new SpreadsheetParseError(
      'File could not be read. Please ensure this is a valid .csv or .xlsx file.'
    );
  }

  if (!['xlsx', undefined].includes(wb.bookType)) {
    let validationErrorMessage = 'Only .csv and .xlsx files are supported.';
    if (wb.bookType) {
      validationErrorMessage += ' This file is of type "' + wb.bookType + '".';
    }
    throw new SpreadsheetParseError(validationErrorMessage);
  }

  const ws = wb.Sheets[wb.SheetNames[0]];
  const wsJson: any[] = XLSX.utils.sheet_to_json(
    ws,
    {
      raw: false,
      dateNF: 'YYYY-MM-DD',
      // Kept so an index into wsJson is the spreadsheet row; dropping them
      // shifts every reported row number after a blank line (PRO-1335). They
      // are skipped below.
      blankrows: true
    }
  );

  // No cells at all; a whitespace cell is not blank.
  const isBlankRow = (rowItem: any) => Object.keys(rowItem).length === 0;

  const hasPopulatedRow = wsJson.some(rowItem => !isBlankRow(rowItem));

  if (hasPopulatedRow) {
    const columnNames = spreadsheetHeaderNames(XLSX, ws).map(cn => cn.toLowerCase());
    for (const requiredColumnNameItem of requiredColumnNames) {
      if (Array.isArray(requiredColumnNameItem)) {
        let hasMatch = false;
        for (const requiredColumnNameOption of requiredColumnNameItem) {
          if (columnNames.includes(requiredColumnNameOption.toLowerCase())) { hasMatch = true; }
        }
        if (!hasMatch) {
          throw new SpreadsheetParseError(
            'This file is missing a required column: "' + requiredColumnNameItem[0] +
            '". Please ensure the spreadsheet begins with a header row ' +
            'containing all required column names.'
          );
        }
      } else {
        if (!columnNames.includes(requiredColumnNameItem.toLowerCase())) {
          throw new SpreadsheetParseError(
            'This file is missing a required column: "' + requiredColumnNameItem +
            '". Please ensure the spreadsheet begins with a header row ' +
            'containing all required column names.'
          );
        }
      }
    }
  }

  for (let rowIdx = 0; rowIdx < wsJson.length; rowIdx++) {
    if (isBlankRow(wsJson[rowIdx])) { continue; }
    processRowFunc(rowIdx, wsJson[rowIdx]);
  }
}
